import repository from '@/repository'
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import { Customer } from '@/models'
import { RootState } from '@/store/types'

const getDefaultState = (): CustomersState => {
  return {
    customerList: [],
    customerListIsLoading: false,
    addCustomerIsLoading: false,
    addCustomerError: false,
    addCustomerErrorMessage: '',
    newCustomerId: 0,
    updateCustomerIsLoading: false,
    updateCustomerError: false,
    updateCustomerErrorMessage: '',
    updatePrimaryContactErrorMessage: null,
    customerStorageData: {},
    customerStorageIsLoading: false,
    downloadReportIsLoading: false,
    customerResourceList: [],
    customerResourceListIsLoading: false,
    customerResourceListError: false,
    customerResourceListErrorMessage: '',
    customerResourceTypes: [],
    addCustomerResourceIsLoading: false,
    addCustomerResourceError: false,
    addCustomerResourceErrorMessage: '',
    deleteCustomerResourceError: false,
    deleteCustomerResourceErrorMessage: '',
  }
}

const state = getDefaultState()

const getters: GetterTree<CustomersState, RootState> = {
  currentCustomer: (state) => (customerId: number) => {
    return state.customerList.find((customer: Customer) => {
      if (customer.customerId === customerId) {
        return customer
      }
    })
  },
  customers: (state) => {
    return state.customerList
  },
  customerList: (state) => {
    const list = state.customerList.map((item) => {
      if (item.address.includes('|')) {
        const updatedString = item.address.replaceAll('|', ' ')
        return { ...item, address: updatedString }
      }
      return item
    })
    return list
  },
}

const mutations: MutationTree<CustomersState> = {
  reset(state) {
    Object.assign(state, getDefaultState())
  },
  setCustomerList(state, value) {
    state.customerList = value
  },
  setCustomerListIsLoading(state, value) {
    state.customerListIsLoading = value
  },
  setAddCustomerIsLoading(state, value) {
    state.addCustomerIsLoading = value
  },
  setAddCustomerError(state, value) {
    state.addCustomerError = value
  },
  setAddCustomerErrorMessage(state, value) {
    state.addCustomerErrorMessage = value
  },
  setNewCustomerId(state, value) {
    state.newCustomerId = value
  },
  setUpdateCustomerIsLoading(state, value) {
    state.updateCustomerIsLoading = value
  },
  setUpdateCustomerError(state, value) {
    state.updateCustomerError = value
  },
  setUpdateCustomerErrorMessage(state, value) {
    state.updateCustomerErrorMessage = value
  },
  setUpdatePrimaryContactErrorMessage(state, value) {
    state.updatePrimaryContactErrorMessage = value
  },
  setUpdatedCustomer(state, value) {
    state.customerList = state.customerList.map((item) => {
      return item.customerId === value.customerId ? value : item
    })
  },
  setUpdatedPrimaryContact(state, value) {
    state.customerList = state.customerList.map((item) => {
      return item.customerId === value.customerId ? value : item
    })
  },
  setCustomerStorage(state, value) {
    state.customerStorageData = value
  },
  setCustomerStorageIsLoading(state, value) {
    state.customerStorageIsLoading = value
  },
  setDownLoadReportIsLoading(state, value) {
    state.downloadReportIsLoading = value
  },
  setCustomerResourceList(state, value) {
    state.customerResourceList = value
  },
  setCustomerResourceListIsLoading(state, value) {
    state.customerResourceListIsLoading = value
  },
  setCustomerResourceListError(state, value) {
    state.customerResourceListError = value
  },
  setCustomerResourceListErrorMessage(state, value) {
    state.customerResourceListErrorMessage = value
  },
  setCustomerResourceTypes(state, value) {
    state.customerResourceTypes = value
  },
  setAddCustomerResourceIsLoading(state, value) {
    state.addCustomerResourceIsLoading = value
  },
  setAddCustomerResourceError(state, value) {
    state.addCustomerResourceError = value
  },
  setAddCustomerResourceErrorMessage(state, value) {
    state.addCustomerResourceErrorMessage = value
  },
  setDeleteCustomerResourceError(state, value) {
    state.deleteCustomerResourceError = value
  },
  setDeleteCustomerResourceErrorMessage(state, value) {
    state.deleteCustomerResourceErrorMessage = value
  },
}

const actions: ActionTree<CustomersState, RootState> = {
  async getCustomerList({ commit }, payload) {
    commit('setCustomerListIsLoading', true)
    return repository.Customer.GetCustomers(payload)
      .then((res) => {
        commit('setCustomerList', res.data.items)
      })
      .catch(() => {
        //error message here
      })
      .finally(() => {
        commit('setCustomerListIsLoading', false)
      })
  },
  async getCustomerListNoLoading({ commit }, payload) {
    return repository.Customer.GetCustomers(payload).then((res) => {
      commit('setCustomerList', res.data.items)
    })
  },
  async getCustomerStorageCapacity({ commit }, payload) {
    commit('setCustomerStorageIsLoading', true)
    return repository.Customer.GetStorageUsage(payload)
      .then((res) => {
        commit('setCustomerStorage', res.data)
      })
      .catch(() => {})
      .finally(() => {
        commit('setCustomerStorageIsLoading', false)
      })
  },
  async addCustomer({ commit }, payload) {
    commit('setAddCustomerIsLoading', true)
    return repository.Customer.Add(payload)
      .then((res) => {
        commit('setNewCustomerId', res.data.customerId)
        commit('setAddCustomerError', false)
      })
      .catch((err) => {
        commit('setAddCustomerError', true)
        commit(
          'setAddCustomerErrorMessage',
          err.response.data.messageLocalizable
        )
      })
      .finally(() => {
        commit('setAddCustomerIsLoading', false)
      })
  },
  async updateCustomer({ commit }, payload) {
    commit('setUpdateCustomerIsLoading', true)
    return repository.Customer.Update(payload)
      .then((res) => {
        commit('setUpdatedCustomer', res.data)
        commit('setUpdateCustomerError', false)
      })
      .catch((err) => {
        commit('setUpdateCustomerError', true)
        commit(
          'setUpdateCustomerErrorMessage',
          err.response.data.messageLocalizable
        )
      })
      .finally(() => {
        commit('setUpdateCustomerIsLoading', false)
      })
  },
  async updatePrimaryUserContact({ commit }, payload) {
    return repository.Customer.UpdatePrimaryUserContact(payload)
      .then((res) => {
        commit('setUpdatedPrimaryContact', res.data)
        commit('setUpdatePrimaryContactErrorMessage', null)
      })
      .catch((err) => {
        commit(
          'setUpdatePrimaryContactErrorMessage',
          err.response.data.messageLocalizable
        )
      })
      .finally(() => {})
  },
  async downloadSensitiveDeviceDetailsReport({ commit }, payload) {
    commit('setDownLoadReportIsLoading', true)
    return repository.Dealer.DownloadSensitiveDeviceDetailsReport(payload)
      .then((res) => {
        return res
      })
      .catch(() => {
        //error message here
      })
      .finally(() => {
        commit('setDownLoadReportIsLoading', false)
      })
  },
  async getCustomerResourceList({ commit }, payload) {
    commit('setCustomerResourceListIsLoading', true)
    commit('setCustomerResourceListError', false)
    return repository.Resource.GetResources(payload)
      .then((res) => {
        commit('setCustomerResourceList', res.data)
      })
      .catch((err) => {
        commit('setCustomerResourceListError', true)
        commit(
          'setCustomerResourceListErrorMessage',
          err.response.data.messageLocalizable
        )
      })
      .finally(() => {
        commit('setCustomerResourceListIsLoading', false)
      })
  },
  async getCustomerResourceTypes({ commit }, payload) {
    commit('setCustomerResourceListIsLoading', true)
    return repository.Resource.GetResourceTypes(payload)
      .then((res) => {
        commit('setCustomerResourceTypes', res.data)
      })
      .catch(() => {})
      .finally(() => {
        commit('setCustomerResourceListIsLoading', false)
      })
  },
  async addCustomerResource({ commit }, payload) {
    commit('setAddCustomerResourceIsLoading', true)
    commit('setAddCustomerResourceError', false)
    return repository.Resource.CreateResource(payload)
      .then((res) => {
        return res.data
      })
      .catch((error) => {
        commit('setAddCustomerResourceError', true)
        commit(
          'setAddCustomerResourceErrorMessage',
          error.response?.data?.messageLocalizable ||
            'toastMessage.resourceFailedUploadFile'
        )
      })
      .finally(() => {
        commit('setAddCustomerResourceIsLoading', false)
      })
  },
  async updateCustomerResource({ commit }, payload) {
    commit('setAddCustomerResourceIsLoading', true)
    commit('setAddCustomerResourceError', false)
    return repository.Resource.UpdateResource(payload)
      .then((res) => {
        return res.data
      })
      .catch((error) => {
        commit('setAddCustomerResourceError', true)
        commit(
          'setAddCustomerResourceErrorMessage',
          error.response?.data?.messageLocalizable ||
            'toastMessage.resourceFailedUploadFile'
        )
      })
      .finally(() => {
        commit('setAddCustomerResourceIsLoading', false)
      })
  },

  async deleteCustomerResource({ commit }, payload) {
    commit('setCustomerResourceListIsLoading', true)
    commit('setDeleteCustomerResourceError', false)
    return repository.Resource.DeleteResource(payload)
      .then((res) => {
        return res.data
      })
      .catch((error) => {
        commit('setDeleteCustomerResourceError', true)
        commit(
          'setDeleteCustomerResourceErrorMessage',
          error.response.data.messageLocalizable
        )
      })
      .finally(() => {
        commit('setCustomerResourceListIsLoading', false)
      })
  },
  isResourceInUse(_, payload) {
    return repository.Resource.IsResourceInUse(payload)
      .then((res) => {
        return res.data
      })
      .catch(() => {
        return false
      })
  },
}

const Customers: Module<CustomersState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}

export default Customers

interface CustomersState {
  customerList: Customer[]
  customerListIsLoading: boolean
  addCustomerIsLoading: boolean
  addCustomerError: boolean
  addCustomerErrorMessage: string
  newCustomerId: number
  updateCustomerIsLoading: boolean
  updateCustomerError: boolean
  updateCustomerErrorMessage: string
  updatePrimaryContactErrorMessage: string | null
  customerStorageData: any
  customerStorageIsLoading: boolean
  downloadReportIsLoading: boolean
  customerResourceList: any[]
  customerResourceListIsLoading: boolean
  customerResourceListError: boolean
  customerResourceListErrorMessage: string | null
  customerResourceTypes: any[]
  addCustomerResourceIsLoading: boolean
  addCustomerResourceError: boolean
  addCustomerResourceErrorMessage: string | null
  deleteCustomerResourceError: boolean
  deleteCustomerResourceErrorMessage: string | null
}
